// src/pages/dashboard/shared/Messages.jsx
import { useState, useEffect, useRef } from 'react';
import api from '../../../lib/api';
import { formatDate } from '../../../lib/helpers';
import { Send, Paperclip, Phone, Video, MoreVertical } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';

export default function Messages() {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchConversations();
  }, []);

  useEffect(() => {
    if (selected) fetchMessages();
  }, [selected]);

  const fetchConversations = async () => {
    try {
      const res = await api.get('/messages/conversations');
      setConversations(res.data.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchMessages = async () => {
    try {
      const res = await api.get(`/messages/conversation/${selected.id}`);
      setMessages(res.data.data || []);
      scrollToBottom();
    } catch (err) {
      console.error(err);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) return;
    try {
      await api.post('/messages', {
        recipient_id: selected.user_id,
        message: newMessage,
      });
      setNewMessage('');
      fetchMessages();
    } catch (err) {
      alert('Failed to send');
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="max-w-7xl mx-auto h-screen pt-10 flex gap-6 grid grid-cols-1 lg:grid-cols-3">
      {/* Conversations */}
      <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
        <div className="p-6 border-b">
          <h2 className="text-2xl font-bold">Messages</h2>
        </div>
        <div className="h-full overflow-y-auto">
          {conversations.map(conv => (
            <button
              key={conv.id}
              onClick={() => setSelected(conv)}
              className={`w-full p-5 text-left hover:bg-gray-50 transition ${selected?.id === conv.id ? 'bg-primary/10' : ''}`}
            >
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-primary text-white rounded-full flex items-center justify-center text-xl font-bold">
                  {conv.name[0]}
                </div>
                <div className="flex-1">
                  <p className="font-bold">{conv.name}</p>
                  <p className="text-sm text-gray-600 truncate">{conv.last_message}</p>
                </div>
                <p className="text-xs text-gray-500">{conv.time}</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Chat Area */}
      <div className="lg:col-span-2 bg-white rounded-3xl shadow-xl flex flex-col">
        {selected ? (
          <>
            <div className="p-6 border-b flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-primary text-white rounded-full flex items-center justify-center font-bold">
                  {selected.name[0]}
                </div>
                <div>
                  <h3 className="font-bold text-xl">{selected.name}</h3>
                  <p className="text-sm text-green-600">Online</p>
                </div>
              </div>
              <div className="flex gap-3">
                <button><Phone size={24} /></button>
                <button><Video size={24} /></button>
                <button><MoreVertical size={24} /></button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {messages.map(msg => (
                <div
                  key={msg.id}
                  className={`flex ${msg.is_mine ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-xs px-5 py-3 rounded-2xl ${msg.is_mine ? 'bg-primary text-white' : 'bg-gray-100'}`}>
                    <p>{msg.message}</p>
                    <p className="text-xs opacity-70 mt-1">{formatDate(msg.created_at, true)}</p>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            <div className="p-6 border-t">
              <div className="flex gap-3">
                <button><Paperclip size={24} className="text-gray-500" /></button>
                <input
                  type="text"
                  value={newMessage}
                  onChange={e => setNewMessage(e.target.value)}
                  onKeyPress={e => e.key === 'Enter' && sendMessage()}
                  placeholder="Type a message..."
                  className="flex-1 px-5 py-3 border rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <button onClick={sendMessage}>
                  <Send size={24} className="text-primary" />
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-400">
            <p className="text-2xl">Select a conversation to start messaging</p>
          </div>
        )}
      </div>
    </div>
  );
}